/**
 * S4 Onda 4 — limbo da fila (modo recepcionista): a lógica PURA compartilhada
 * BE ↔ FE. Extraída do engine `queue-limbo.util.ts` para `@lume/shared` na S7
 * PR2 (mesma disciplina "lógica viaja" de `resolveAppointmentReminderPolicy`):
 *
 *   - `resolveEscalationLimboPolicy` — parse defensivo do umbrella por-tenant
 *     `Tenant.escalationLimboPolicy`. O editor da policy (`QueueLimboTab`, web)
 *     mostra EXATAMENTE o que o runtime fará (resolução efetiva única BE+FE),
 *     igual ao `resolveAppointmentReminderPolicy`/`RemindersTab`.
 *   - `classifyEscalationReason` — a matriz motivo→classe de postura (L1a). É a
 *     ÚNICA fonte da regra; o banner `queue-held` do operador (web+mobile) deriva
 *     a copy a partir da classe SEM re-hardcodear os conjuntos de motivo (evita o
 *     drift copy-vs-autoritativo que a revisão adversarial pega).
 *
 * O engine `queue-limbo.util.ts` re-exporta estes símbolos (behavior-preserving)
 * e mantém localmente as copies customer-facing + a decisão de turno (que não
 * têm consumidor de FE).
 */
export const QUEUE_RECEPTIONIST_CAPABILITY = 'ai-queue-receptionist';
const QUEUE_EXPIRY_POSTURES = new Set(['expire-after-window', 'never-expire']);
export const ESCALATION_LIMBO_POLICY_DEFAULTS = {
    maxQueueMessages: 5,
    minIntervalSec: 90,
    responseDelaySec: 10,
    interveneAfterSec: 0,
    // Espera Viva PR-3 — marcos (min); defaults da spec §4.3.
    updateAfterMin: 5,
    internalAlertAfterMin: 15,
    optionsAfterMin: 30,
    aiResumeAfterMin: 10,
    // F3 — 24h EM JANELA. Mesmo número de antes; a régua de contagem é que mudou.
    queueExpiryWindowHours: 24,
};
export const ESCALATION_LIMBO_POLICY_CLAMPS = {
    maxQueueMessages: [0, 50],
    minIntervalSec: [0, 3600],
    responseDelaySec: [0, 120],
    interveneAfterSec: [0, 86_400],
    updateAfterMin: [1, 240],
    internalAlertAfterMin: [1, 480],
    optionsAfterMin: [1, 1440],
    aiResumeAfterMin: [1, 240],
    // Teto de 90 dias de janela: acima disso, «nunca expira» é a postura honesta.
    queueExpiryWindowHours: [1, 2160],
};
/** Clamp de float SEM floor (urgencyFactor é fração). */
function clampedFloat(raw, dflt, min, max) {
    const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : dflt;
    return Math.min(max, Math.max(min, n));
}
function clampedNumber(raw, key) {
    const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : ESCALATION_LIMBO_POLICY_DEFAULTS[key];
    const [min, max] = ESCALATION_LIMBO_POLICY_CLAMPS[key];
    return Math.min(max, Math.max(min, Math.floor(n)));
}
/**
 * Parse defensivo do `Tenant.escalationLimboPolicy` (Json cru). `null`/malformado ⇒
 * gates OFF + defaults numéricos safe. Gates só ligam com `true` LITERAL (truthy
 * não-boolean não liga — mesmo rigor do resto das policies umbrella).
 */
export function resolveEscalationLimboPolicy(raw) {
    const obj = typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw
        : {};
    return {
        ackEnabled: obj.ackEnabled === true,
        receptionistEnabled: obj.receptionistEnabled === true,
        maxQueueMessages: clampedNumber(obj.maxQueueMessages, 'maxQueueMessages'),
        minIntervalSec: clampedNumber(obj.minIntervalSec, 'minIntervalSec'),
        responseDelaySec: clampedNumber(obj.responseDelaySec, 'responseDelaySec'),
        interveneAfterSec: clampedNumber(obj.interveneAfterSec, 'interveneAfterSec'),
        postureV2Enabled: obj.postureV2Enabled === true,
        voiceRenewalEnabled: obj.voiceRenewalEnabled === true,
        milestonesEnabled: obj.milestonesEnabled === true,
        updateAfterMin: clampedNumber(obj.updateAfterMin, 'updateAfterMin'),
        internalAlertAfterMin: clampedNumber(obj.internalAlertAfterMin, 'internalAlertAfterMin'),
        optionsAfterMin: clampedNumber(obj.optionsAfterMin, 'optionsAfterMin'),
        aiResumeAfterMin: clampedNumber(obj.aiResumeAfterMin, 'aiResumeAfterMin'),
        urgencyFactor: clampedFloat(obj.urgencyFactor, 0.5, 0.1, 1),
        // Decisão TL 03/Ago (spec §4.1): a oferta única do C3 nasce LIGADA — só
        // `false` literal desliga (postura inversa dos gates: default é o veredito).
        humanRequestedOfferEnabled: obj.humanRequestedOfferEnabled !== false,
        // F3 — postura do teto. Valor fora do conjunto cai no DEFAULT (não em «nunca
        // expira»): fail-safe aqui é preservar o comportamento conhecido, não afrouxar.
        queueExpiryPosture: typeof obj.queueExpiryPosture === 'string' &&
            QUEUE_EXPIRY_POSTURES.has(obj.queueExpiryPosture)
            ? obj.queueExpiryPosture
            : 'expire-after-window',
        queueExpiryWindowHours: clampedNumber(obj.queueExpiryWindowHours, 'queueExpiryWindowHours'),
        // F5 — opt-in explícito (`true` literal), nunca `!== false`.
        presenceOpensWindow: obj.presenceOpensWindow === true,
        // §D4 — id de usuário não vazio; qualquer outra coisa é «não configurado».
        teamTaskOwnerUserId: typeof obj.teamTaskOwnerUserId === 'string' && obj.teamTaskOwnerUserId.trim() !== ''
            ? obj.teamTaskOwnerUserId.trim()
            : null,
    };
}
// ─── Matriz motivo→classe (Q-L1a — vocabulário REAL do código) ───────────────
const CLASS_1_REASONS = new Set([
    // A IA não falhou de conduta; falhou o instrumento / o assunto saiu do escopo.
    'tool-unavailable',
    'tool-failure',
    'out-of-scope',
    'customer-confused',
]);
const CLASS_3_REASONS = new Set([
    // Proteção/segurança: qualquer condução adicional é risco. Ack único e MUDA.
    'level-4',
    'policy-conflict',
]);
/**
 * Classe de postura pelo MOTIVO persistido da escalação. Prefixos `state-guard:*`/
 * `promise-gate:*` (guards de 2ª geração) são classe 2 — a IA já demonstrou mau
 * funcionamento NESTA conversa; presença institucional, não condução. Motivo
 * desconhecido/ausente ⇒ classe 2 (conservador).
 */
export function classifyEscalationReason(reason) {
    if (reason === null || reason === undefined || reason === '')
        return 2;
    if (CLASS_1_REASONS.has(reason))
        return 1;
    if (CLASS_3_REASONS.has(reason))
        return 3;
    return 2;
}
/**
 * Matriz v2 (gated: `opts.v2 !== true` delega para a v1, byte-compat — todos os
 * call-sites atuais seguem intocados). Ordem dos vetos, do mais forte:
 * proteção (3) → cliente exigiu (2) → IA ofertou (1) → técnica incl.
 * malformed-output (1) → conservador (2). `malformed-output` na v1 caía em 2
 * («cartório») — medido em 03/Ago custando a resposta que o cliente pedia; na
 * v2 é falha do INSTRUMENTO (parse/formato), não de conduta ⇒ classe 1.
 * Guards de 2ª geração (`state-guard:*`/`promise-gate:*`) permanecem 2: ali a
 * IA demonstrou mau funcionamento de CONDUTA nesta conversa.
 */
/**
 * Espera Viva PR-2/PR-3 — nome HUMANO e NEUTRO do motivo da escalação (fala,
 * pendência materializada e nudge de retomada). Fora do mapa ⇒ null (nunca
 * inventa nome). Compartilhado BE (conductor + cron de marcos).
 */
export function humanizeEscalationReason(reason) {
    if (reason == null)
        return null;
    if (reason === 'malformed-output' || reason === 'tool-failure' || reason === 'tool-unavailable') {
        return 'uma dificuldade técnica';
    }
    if (reason === 'out-of-scope' || reason === 'customer-confused') {
        return 'um assunto que precisa da equipe';
    }
    if (reason === 'customer-requested-human')
        return 'a transferência que você pediu';
    return null;
}
export function classifyQueuePosture(reason, origin, opts) {
    if (opts?.v2 !== true)
        return classifyEscalationReason(reason);
    if (reason !== null && reason !== undefined && CLASS_3_REASONS.has(reason))
        return 3;
    if (origin === 'customer-request')
        return 2;
    if (origin === 'ai-offer')
        return 1;
    if (reason !== null && reason !== undefined && reason !== '') {
        if (CLASS_1_REASONS.has(reason) || reason === 'malformed-output')
            return 1;
    }
    return 2;
}
//# sourceMappingURL=escalation-limbo.js.map